import { GLOSSARY, SPREADS, SUIT_LABELS, type Suit } from '@tarot/shared';
import { useTrack } from '../../lib/track';
import { useStaticPageMeta } from '../../lib/meta';
import { PrimaryLink, SecondaryLink } from '../../components/Buttons';

/**
 * 타로 용어 사전 (/glossary) — "슈트가 뭔가요?" 같은 첫 진입 장벽을 없앤다.
 * 용어 정의는 shared GLOSSARY 단일 출처, 슈트·스프레드 표는 실제 데이터에서 생성.
 */
export function GlossaryPage() {
  useTrack('view:glossary');
  useStaticPageMeta('/glossary');

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">타로 용어 사전</h1>
        <p className="text-sm leading-relaxed text-moon-dim">
          해석을 읽다 보면 아르카나·슈트 같은 낯선 말이 나와요. 몰라도 타로를 보는 데 지장은 없지만,
          알고 보면 결과가 훨씬 잘 읽힙니다.
        </p>
      </header>

      {/* 목차 — 앵커로 바로 이동 */}
      <nav className="flex flex-wrap gap-1.5">
        {GLOSSARY.map((t) => (
          <a
            key={t.id}
            href={`#${t.id}`}
            className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs transition hover:border-gold/50 hover:text-gold"
          >
            {t.term}
          </a>
        ))}
      </nav>

      {GLOSSARY.map((t) => (
        <section key={t.id} id={t.id} className="flex scroll-mt-6 flex-col gap-2">
          <h2 className="flex flex-wrap items-baseline gap-2 text-lg font-bold">
            {t.term}
            {t.alias && <span className="text-xs font-normal text-moon-dim">{t.alias}</span>}
          </h2>
          <p className="text-sm font-medium text-gold">{t.short}</p>
          {t.body.map((p, i) => (
            <p key={i} className="text-sm leading-relaxed text-moon-dim">
              {p}
            </p>
          ))}

          {/* 슈트·스프레드는 말로만 설명하면 안 와닿아서 실제 데이터를 표로 함께 보여준다 */}
          {t.id === 'suit' && (
            <ul className="mt-1 grid grid-cols-2 gap-2">
              {(Object.keys(SUIT_LABELS) as Suit[]).map((s) => (
                <li
                  key={s}
                  className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm"
                >
                  <b className="text-gold">{SUIT_LABELS[s].ko}</b>
                  <span className="block text-xs text-moon-dim">{SUIT_LABELS[s].theme}</span>
                </li>
              ))}
            </ul>
          )}
          {t.id === 'spread' && (
            <ul className="mt-1 flex flex-col gap-2">
              {Object.values(SPREADS).map((sp) => (
                <li key={sp.id} className="rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                  <b className="text-sm text-gold">{sp.nameKo}</b>
                  <span className="block text-xs text-moon-dim">
                    {sp.positions.map((p) => p.nameKo).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <div className="mx-auto flex w-full max-w-xs flex-col gap-2">
        <PrimaryLink to="/solo">타로 보러 가기</PrimaryLink>
        <SecondaryLink to="/cards">카드 78장 의미 보기</SecondaryLink>
      </div>
    </div>
  );
}
